import { ChannelIcon, useFormat } from '@ly360/ui';
import { GitMerge } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { handleLabel } from './handles';

export interface PersonSummary {
  id: string;
  displayName: string;
  nameArabic: string | null;
  nameLatin: string | null;
  jobTitle: string | null;
  city: string | null;
  country: string | null;
  lifecycleStage: string;
  status: string;
  createdAt: string;
  ownerName: string | null;
  handles: { type: string; value: string; verified: boolean }[];
  companies: string[];
  conversations: number;
  cases: number;
  deals: number;
  tasks: number;
  lastContactAt: string | null;
  channels: string[];
  lastMessage: { text: string | null; channel: string; direction: string; at: string } | null;
}

const norm = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Two records side by side with everything a reviewer needs to decide whether they are the same person.
 * Details both records share are marked "Same".
 */
export function ComparePeople({
  a,
  b,
  onKeep,
  busy,
}: {
  a: PersonSummary;
  b: PersonSummary;
  /** Called with the record to keep (the other one is merged into it). */
  onKeep?: (keep: PersonSummary, drop: PersonSummary) => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const handles = (p: PersonSummary, types: string[]) => p.handles.filter((h) => types.includes(h.type));
  const shared = (types: string[]) => {
    const av = new Set(handles(a, types).map((h) => h.value));
    return new Set(
      handles(b, types)
        .map((h) => h.value)
        .filter((v) => av.has(v)),
    );
  };

  type Row = {
    key: string;
    label: string;
    render: (p: PersonSummary) => ReactNode;
    text?: (p: PersonSummary) => string;
    /** Hidden when neither record has a value (keeps the comparison to what matters). */
    has?: (p: PersonSummary) => boolean;
  };
  const handleRow = (key: string, types: string[]): Row => {
    const same = shared(types);
    return {
      key,
      label: t(`contacts.compare.${key}`),
      render: (p) => {
        const list = handles(p, types);
        if (!list.length) return <span className="faint">—</span>;
        return (
          <ul className="list-plain cmp-handles">
            {list.map((h) => (
              <li
                key={h.value}
                className={same.has(h.value) ? 'is-same' : ''}
                dir={h.type === 'web_user_id' ? 'auto' : 'ltr'}
              >
                {handleLabel(t, h.type, h.value)}
                {h.verified && <span className="badge badge-success">{t('crm.person.verified')}</span>}
              </li>
            ))}
          </ul>
        );
      },
      text: (p) => (shared(types).size ? 'same' : `${p.id}`),
      has: (p) => handles(p, types).length > 0,
    };
  };
  const textRow = (key: string, get: (p: PersonSummary) => string | null | undefined): Row => ({
    key,
    label: t(`contacts.compare.${key}`),
    render: (p) => {
      const v = get(p);
      return v ? <span dir="auto">{v}</span> : <span className="faint">—</span>;
    },
    text: (p) => norm(get(p) ?? ''),
    has: (p) => !!get(p),
  });

  const rows: Row[] = [
    textRow('name', (p) => p.displayName),
    textRow('arabicName', (p) => p.nameArabic),
    handleRow('phones', ['phone']),
    handleRow('emails', ['email']),
    handleRow('whatsapp', ['wa_bsuid']),
    handleRow('website', ['web_user_id']),
    textRow('companies', (p) => p.companies.join(', ')),
    textRow('jobTitle', (p) => p.jobTitle),
    textRow('city', (p) => [p.city, p.country].filter(Boolean).join(', ')),
    textRow('stage', (p) => t(`crm.lifecycle.${p.lifecycleStage}`)),
    textRow('owner', (p) => p.ownerName),
    {
      key: 'channels',
      label: t('contacts.compare.channels'),
      render: (p) =>
        p.channels.length ? (
          <span className="chips">
            {p.channels.map((c) => (
              <span key={c} className="channel-badge" title={t(`channels.${c}`)}>
                <ChannelIcon channel={c} size={12} />
                <span>{t(`channels.${c}`)}</span>
              </span>
            ))}
          </span>
        ) : (
          <span className="faint">—</span>
        ),
    },
    {
      key: 'activity',
      label: t('contacts.compare.activity'),
      render: (p) => (
        <span>
          {t('contacts.compare.counts', {
            conversations: p.conversations,
            cases: p.cases,
            deals: p.deals,
            tasks: p.tasks,
          })}
        </span>
      ),
    },
    {
      key: 'lastContact',
      label: t('contacts.compare.lastContact'),
      render: (p) =>
        p.lastContactAt ? (
          <span title={fmt.dateTimeZoned(p.lastContactAt)}>{fmt.relative(p.lastContactAt)}</span>
        ) : (
          <span className="faint">—</span>
        ),
    },
    {
      key: 'lastMessage',
      label: t('contacts.compare.lastMessage'),
      render: (p) =>
        p.lastMessage?.text ? (
          <span className="cmp-message">
            <ChannelIcon channel={p.lastMessage.channel} size={11} />
            <span dir="auto">“{p.lastMessage.text}”</span>
            <span className="faint">{fmt.dateTime(p.lastMessage.at)}</span>
          </span>
        ) : (
          <span className="faint">—</span>
        ),
    },
    {
      key: 'created',
      label: t('contacts.compare.created'),
      render: (p) => <span>{fmt.dateTime(p.createdAt)}</span>,
    },
  ];

  return (
    <div className="cmp-scroll">
      <table className="cmp-table" data-testid="compare-people">
        <thead>
          <tr>
            <th scope="col" className="cmp-label" />
            {[a, b].map((p, i) => (
              <th key={p.id} scope="col" data-testid="compare-col">
                <Link to={`/people/${p.id}`} dir="auto">
                  {p.displayName}
                </Link>
                {onKeep && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy}
                    onClick={() => onKeep(p, i === 0 ? b : a)}
                    data-testid="keep-this"
                  >
                    <GitMerge size={14} />
                    {t('contacts.duplicates.keepThis')}
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows
            .filter((r) => !r.has || r.has(a) || r.has(b))
            .map((r) => {
              const same = r.text
                ? r.key.match(/phones|emails|whatsapp|website/)
                  ? r.text(a) === 'same'
                  : !!r.text(a) && r.text(a) === r.text(b)
                : false;
              return (
                <tr key={r.key} className={same ? 'is-same' : ''}>
                  <th scope="row" className="cmp-label">
                    {r.label}
                    {same && (
                      <span className="badge badge-success cmp-same">{t('contacts.compare.same')}</span>
                    )}
                  </th>
                  <td>{r.render(a)}</td>
                  <td>{r.render(b)}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
